import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from './Header';
import Footer from './Footer';
import { ArrowLeft, Save, Clock, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PIPELINE_STEPS = [
  'anagrafica', 'negoziazione', 'documentazione', 'contratto', 'chiuso_vinto', 'chiuso_perso'
];

const LeadDetailPage = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [history, setHistory] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchLead();
    fetchHistory();
  }, [leadId]);

  const fetchLead = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BACKEND_URL}/api/crm/leads/${leadId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setLead(data);
      } else {
        setError('Lead non trovato');
      }
    } catch (err) {
      setError('Errore caricamento lead');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BACKEND_URL}/api/crm/leads/${leadId}/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      }
    } catch (err) {
      console.error('Errore caricamento storico:', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BACKEND_URL}/api/crm/leads/${leadId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(lead)
      });

      if (response.ok) {
        setSuccess('Lead aggiornato con successo!');
        fetchHistory(); // Ricarica lo storico
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Errore aggiornamento lead');
      }
    } catch (err) {
      setError('Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setLead({ ...lead, [field]: value });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <p className="text-white">Lead non trovato</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      <Header />
      
      <div className="flex-grow container mx-auto px-4 pt-48 pb-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/leads/pipeline')}
            className="flex items-center text-gray-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Torna alla Pipeline
          </button>
          
          <h1 className="text-3xl font-bold text-white mb-2">Dettaglio Lead</h1>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 mb-6">
            <p className="text-red-400">{error}</p>
          </div>
        )}
        
        {success && (
          <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-4 mb-6">
            <p className="text-green-400">{success}</p>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left - Lead Data */}
          <div className="lg:col-span-2 space-y-6">
            {/* Anagrafica */}
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <h2 className="text-xl font-bold text-white mb-4">Anagrafica</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Nome e Cognome *</label>
                  <input
                    type="text"
                    value={lead.nome_cognome || ''}
                    onChange={(e) => handleChange('nome_cognome', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <input
                    type="email"
                    value={lead.email || ''}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Telefono</label>
                  <input
                    type="tel"
                    value={lead.telefono || ''}
                    onChange={(e) => handleChange('telefono', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Azienda</label>
                  <input
                    type="text"
                    value={lead.azienda || ''}
                    onChange={(e) => handleChange('azienda', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Pipeline Step</label>
                  <select
                    value={lead.pipeline_step || 'anagrafica'}
                    onChange={(e) => handleChange('pipeline_step', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
                  >
                    {PIPELINE_STEPS.map(step => (
                      <option key={step} value={step}>{step}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Fonte</label>
                  <select
                    value={lead.fonte || 'sito_web'}
                    onChange={(e) => handleChange('fonte', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
                  >
                    <option value="sito_web">Sito Web</option>
                    <option value="linkedin">LinkedIn</option>
                    <option value="referral">Referral</option>
                    <option value="telefono">Chiamata Telefonica</option>
                    <option value="customer_base">Cliente Esistente</option>
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">Note Venditore</label>
                <textarea
                  value={lead.note_venditore || ''}
                  onChange={(e) => handleChange('note_venditore', e.target.value)}
                  rows="4"
                  className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full px-6 py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg font-bold text-lg transition-colors flex items-center justify-center space-x-2"
            >
              <Save size={24} />
              <span>{saving ? 'Salvataggio...' : 'Salva Modifiche'}</span>
            </button>
          </div>

          {/* Right - History */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center">
              <Clock className="w-6 h-6 mr-2 text-yellow-400" />
              Storico Modifiche
            </h2>

            <div className="space-y-3">
              {history.length === 0 ? (
                <p className="text-gray-400 text-sm">Nessuna modifica registrata</p>
              ) : (
                history.map((entry, idx) => (
                  <div key={idx} className="bg-gray-700 rounded-lg p-3 border-l-4 border-blue-500">
                    <p className="text-sm text-white font-medium">{entry.action}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      <User size={12} className="inline mr-1" />
                      {entry.user} - {new Date(entry.timestamp).toLocaleString('it-IT')}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LeadDetailPage;